import { Injectable } from '@angular/core';
import { AccountsService } from '@epicuro-next/data/accounts';
import { IUser, UserRole, UsersApiService } from '@epicuro-next/data/users';
import { Contact } from '@epicuro-next/models/contacts';
import { ConnectionStatus } from '@epicuro-next/models/users';
import { selectCurrentBasketAccounts } from '@epicuro-next/state/basket-state';
import {
  selectUserEmail,
  selectUserRoles,
} from '@epicuro-next/state/user-state';
import { isNonNullish } from '@epicuro-next/utilities';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilKeyChanged,
  filter,
  map,
  Observable,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ContactsService {
  private readonly userEmail$ = this.store.select(selectUserEmail);
  private readonly isCurrentlyLoading$ = new BehaviorSubject<boolean>(false);

  private readonly userRolesCached$ = new BehaviorSubject<readonly UserRole[]>(
    [],
  );
  private readonly isUserOrBuyer$ = this.userRolesCached$.pipe(
    map((roles) => this.isUserOrBuyer(roles)),
  );
  private readonly isSalesRep$ = this.userRolesCached$.pipe(
    map((roles) => this.isSalesRepresentative(roles)),
  );

  private readonly salesRepresentativesCached$ = new BehaviorSubject<
    Contact[] | null
  >(null);

  private readonly buyersCached$ = new BehaviorSubject<Contact[] | null>(null);

  private readonly accountIds$ = this.store
    .select(selectCurrentBasketAccounts)
    .pipe(
      map((accounts) => {
        const key = accounts
          .map((account) => account.accountId)
          .sort()
          .join(',');

        return {
          accounts,
          key,
        };
      }),
      distinctUntilKeyChanged('key'),
      map(({ accounts }) => accounts.map((account) => account.accountId)),
    );

  private readonly contactsCached$ = new BehaviorSubject<Contact[] | null>(
    null,
  );

  public readonly isLoading$ = this.isCurrentlyLoading$.asObservable();
  public readonly contacts$ = this.contactsCached$.asObservable();

  constructor(
    private readonly accountsService: AccountsService,
    private readonly store: Store,
    private readonly userService: UsersApiService,
  ) {
    this.setupSalesRepresentatives();
    this.setupBuyers();
    this.setupUserRoles();
    this.setupContacts();
  }

  private setupSalesRepresentatives(): void {
    combineLatest([this.accountIds$, this.isUserOrBuyer$])
      .pipe(
        switchMap(([accountIds, isUserOrBuyer]) => {
          if (accountIds && isUserOrBuyer) {
            this.isCurrentlyLoading$.next(true);
            return this.accountsService.getSalesRepresentatives(accountIds);
          }
          return of([]);
        }),
        map((salesRepresentatives) => salesRepresentatives.map(this.toContact)),
      )
      .subscribe((salesRepresentatives) => {
        this.salesRepresentativesCached$.next(salesRepresentatives);
      });
  }

  private setupBuyers(): void {
    combineLatest([this.accountIds$, this.isSalesRep$])
      .pipe(
        switchMap(([accountIds, isSalesRep]) => {
          if (accountIds && isSalesRep) {
            this.isCurrentlyLoading$.next(true);
            return this.userService.getAccountsUsers(accountIds);
          }
          return of([]);
        }),
        map((accountUsers) =>
          accountUsers.map((au) => {
            const contact: Contact = {
              accounts: [],
              connectionStatus: ConnectionStatus.NOTAVAILABLE,
              displayName: au.displayName,
              email: au.emailAddress,
              userId: au.id,
              imageLink: au.profilePictureUrl ?? '',
            };
            return contact;
          }),
        ),
      )
      .subscribe((buyers) => {
        this.buyersCached$.next(buyers);
      });
  }

  private setupUserRoles(): void {
    this.store
      .select(selectUserRoles)
      .pipe(
        map((userRoles) => {
          return { key: userRoles.join(','), userRoles };
        }),
        distinctUntilKeyChanged('key'),
        map(({ userRoles }) => userRoles),
      )
      .subscribe((userRoles) => {
        this.userRolesCached$.next(userRoles);
      });
  }

  private setupContacts(): void {
    const contacts$: Observable<Contact[]>[] = [
      this.salesRepresentativesCached$.pipe(filter(isNonNullish)),
      this.buyersCached$.pipe(filter(isNonNullish)),
    ];

    combineLatest(contacts$)
      .pipe(
        withLatestFrom(this.userEmail$),
        tap(() => {
          this.isCurrentlyLoading$.next(false);
        }),
        map(([contacts2d, userEmail]) => {
          return contacts2d.reduce(
            (acc, contacts) =>
              acc.concat(
                contacts.filter((contact) => {
                  return contact.email !== userEmail;
                }),
              ),
            [],
          );
        }),
        map((contacts) => {
          const emails = new Set<string>();
          return contacts.filter((contact) => {
            if (emails.has(contact.email)) {
              return false;
            }
            emails.add(contact.email);
            return true;
          });
        }),
      )
      .subscribe((contacts) => {
        this.contactsCached$.next(contacts);
      });
  }

  private isUserOrBuyer(userRoles: readonly UserRole[]): boolean {
    if (userRoles.length === 0) {
      return true;
    }

    const testForRoles: UserRole[] = [
      UserRole.Administrator,
      UserRole.Buyer,
      UserRole.User,
    ];

    return testForRoles.some((role) => userRoles.includes(role));
  }

  private isSalesRepresentative(userRoles: readonly UserRole[]): boolean {
    const testForRoles: UserRole[] = [
      UserRole.Administrator,
      UserRole.SalesRepresentative,
    ];

    return testForRoles.some((role) => userRoles.includes(role));
  }

  private toContact(user: IUser): Contact {
    return {
      connectionStatus: user.connectionStatus ?? ConnectionStatus.NOTAVAILABLE,
      displayName: user.displayName,
      email: user.email,
      imageLink: user.imageLink,
      userId: user.userId,
      accounts: [],
    };
  }
}
